import {
  createFileRoute,
  Link,
  useNavigate,
  useRouter,
  useBlocker,
  redirect,
} from '@tanstack/react-router';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {ArrowLeft, Loader2} from 'lucide-react';
import {
  getEventListForEdit,
  updateEventList,
  updateManualListEvents,
} from '@/server/savedEvent';
import {updateSmartListFilter, previewSmartList} from '@/server/smartList';
import {ManualListEventEditor} from '@/components/manualListEventEditor/manualListEventEditor';
import {searchEventTags, getSystemTags} from '@/server/eventTags';
import {searchOrganizers} from '@/server/organizer';
import {Button} from '@/components/ui/button';
import {Input} from '@/components/ui/input';
import {Label} from '@/components/ui/label';
import {Textarea} from '@/components/ui/textarea';
import {
  Select,
  SelectItem,
  SelectPopup,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {ImageDropzone} from '@/components/imageDropzone/imageDropzone';
import {
  AlertDialog,
  AlertDialogPopup,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogClose,
} from '@/components/ui/alert-dialog';
import {Typography} from '@/components/ui/typography';
import {toastManager} from '@/components/ui/toast';
import {toTitleCase} from '@/utils/displayFormatUtils';
import {EventFilterBuilder} from '@/components/eventFilter/eventFilterBuilder';
import {EventFilterMatchCount} from '@/components/eventFilter/eventFilterMatchCount';
import {
  EventFilterPreviewSidebar,
  MobilePreviewDrawer,
} from '@/components/eventFilter/eventFilterPreviewSidebar';
import {EventFilter} from '@/apiTypes/eventFilter';
import type {Event as EventApi} from '@/apiTypes/event';
import {toMapEvent} from '@/utils/toMapEvent';
import {MobileEventDetailCard} from '@/components/mobileSearch/mobileEventDetailCard';
import {
  EMPTY_EVENT_FILTER_DRAFT,
  eventFilterDraftToFilter,
  eventFilterToDraft,
  type EventFilterDraft,
} from '@/apiTypes/eventFilterDraft';

const PREVIEW_CAP = 50;
const PREVIEW_DEBOUNCE_MS = 350;

export const Route = createFileRoute('/dashboard/lists/$listId/edit')({
  loader: async ({params, context}) => {
    const list = await getEventListForEdit({data: params.listId});
    if (list.kind === 'SMART') {
      if (!context.featureFlags['advanced-filters']) {
        throw redirect({
          to: '/dashboard/lists/$listId',
          params: {listId: list.id},
          search: {page: 1, pageSize: 12},
        });
      }
    }
    return list;
  },
  errorComponent: EditListNotFound,
  component: EditListPage,
});

function useDebouncedValue<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

type PreviewState =
  | {kind: 'idle'}
  | {kind: 'loading'}
  | {kind: 'loaded'; events: EventApi[]; totalCount: number}
  | {kind: 'error'; message: string};

const visibilityOptions = [
  {value: 'PRIVATE', label: 'Private'},
  {value: 'PUBLIC', label: 'Public'},
];

/**
 * Edit view for event list metadata.
 * Pre-populates form with current list data, saves changes via updateEventList.
 * For SMART lists, additionally renders an EventFilterBuilder section
 * with its own save button calling updateSmartListFilter.
 */
function EditListPage() {
  const listData = Route.useLoaderData();
  const router = useRouter();
  const navigate = useNavigate();
  const {listId} = Route.useParams();

  const [name, setName] = useState(listData.name);
  const [description, setDescription] = useState(listData.description ?? '');
  const [coverImageUrl, setCoverImageUrl] = useState(
    listData.coverImageUrl ?? ''
  );
  const [visibility, setVisibility] = useState<'PRIVATE' | 'PUBLIC'>(
    listData.visibility
  );
  const [loading, setLoading] = useState(false);

  const initialDraft = useMemo<EventFilterDraft>(() => {
    if (listData.kind !== 'SMART') return EMPTY_EVENT_FILTER_DRAFT;
    const parsed = EventFilter.safeParse(listData.filter);
    if (!parsed.success) return EMPTY_EVENT_FILTER_DRAFT;
    return eventFilterToDraft(
      parsed.data,
      listData.tagNamesById,
      listData.organizerNamesById
    );
  }, [
    listData.filter,
    listData.kind,
    listData.tagNamesById,
    listData.organizerNamesById,
  ]);

  const [draft, setDraft] = useState<EventFilterDraft>(initialDraft);
  const [previewState, setPreviewState] = useState<PreviewState>({
    kind: 'idle',
  });
  const [previewDrawerOpen, setPreviewDrawerOpen] = useState(false);
  const [systemTags, setSystemTags] = useState<
    Array<{id: string; name: string}>
  >([]);

  useEffect(() => {
    getSystemTags().then(setSystemTags);
  }, []);

  const [eventChanges, setEventChanges] = useState<{
    addedIds: string[];
    removedIds: string[];
  }>({addedIds: [], removedIds: []});
  const eventResetRef = useRef<(() => void) | null>(null);

  const handleEventDirtyChange = useCallback(
    (addedIds: string[], removedIds: string[]) => {
      setEventChanges({addedIds, removedIds});
    },
    []
  );

  const isSmart = listData.kind === 'SMART';

  const isEventsDirty =
    !isSmart &&
    (eventChanges.addedIds.length > 0 || eventChanges.removedIds.length > 0);

  const isMetadataDirty =
    name !== listData.name ||
    description !== (listData.description ?? '') ||
    coverImageUrl !== (listData.coverImageUrl ?? '') ||
    visibility !== listData.visibility;

  const isFilterDirty =
    isSmart && JSON.stringify(draft) !== JSON.stringify(initialDraft);

  const debouncedDraft = useDebouncedValue(draft, PREVIEW_DEBOUNCE_MS);
  const filter = useMemo(
    () => eventFilterDraftToFilter(debouncedDraft),
    [debouncedDraft]
  );

  const isDirty = isMetadataDirty || isFilterDirty || isEventsDirty;
  const canSave =
    isMetadataDirty || (isFilterDirty && !!filter) || isEventsDirty;
  const didSaveRef = useRef(false);

  const blocker = useBlocker({
    shouldBlockFn: () => isDirty && !didSaveRef.current,
    enableBeforeUnload: () => isDirty,
    withResolver: true,
  });

  useEffect(() => {
    if (listData.kind !== 'SMART' || !filter) {
      setPreviewState({kind: 'idle'});
      return;
    }
    let cancelled = false;
    setPreviewState({kind: 'loading'});
    previewSmartList({data: {filter}})
      .then((res) => {
        if (!cancelled) {
          setPreviewState({
            kind: 'loaded',
            events: res.events,
            totalCount: res.totalCount,
          });
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setPreviewState({
            kind: 'error',
            message: err instanceof Error ? err.message : 'Unknown error',
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [filter, listData.kind]);

  const searchTags = useCallback(
    (query: string) => searchEventTags({data: {query}}),
    []
  );

  const handleSearchOrganizers = useCallback(
    (query: string) =>
      searchOrganizers({data: {query}}).then((results) =>
        results.map((o) => ({id: o.id, name: o.name}))
      ),
    []
  );

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || !isDirty) return;

    setLoading(true);
    try {
      const saves: Array<Promise<unknown>> = [];
      if (isMetadataDirty) {
        saves.push(
          updateEventList({
            data: {
              listId,
              name: name.trim(),
              description: description.trim() || null,
              coverImageUrl: coverImageUrl || null,
              visibility,
            },
          })
        );
      }
      if (isFilterDirty && filter) {
        saves.push(updateSmartListFilter({data: {listId, filter}}));
      }
      if (isEventsDirty) {
        saves.push(
          updateManualListEvents({
            data: {
              listId,
              addEventIds: eventChanges.addedIds,
              removeEventIds: eventChanges.removedIds,
            },
          })
        );
      }
      if (saves.length === 0) return;
      await Promise.all(saves);
      didSaveRef.current = true;
      eventResetRef.current?.();
      toastManager.add({title: 'List updated', type: 'success'});
      router.invalidate();
      navigate({
        to: '/dashboard/lists/$listId',
        params: {listId},
        search: {page: 1, pageSize: 12},
      });
    } catch (err) {
      toastManager.add({
        title: err instanceof Error ? err.message : 'Failed to update list',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  }


  const renderEventCard = (event: EventApi) => (
    <MobileEventDetailCard key={event.id} event={toMapEvent(event)} />
  );

  const editColumn = (
    <form
      id="edit-list-form"
      onSubmit={handleSubmit}
      className="flex flex-col gap-5"
    >
      <div className="flex flex-col gap-1.5">
        <Label>Cover Image</Label>
        <ImageDropzone value={coverImageUrl} onChange={setCoverImageUrl} />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="list-name">Name *</Label>
        <Input
          id="list-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={100}
          required
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="list-description">Description</Label>
        <Textarea
          id="list-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Describe your list..."
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label>Visibility</Label>
        <Select
          value={visibility}
          onValueChange={(value) => {
            if (value === 'PRIVATE' || value === 'PUBLIC') {
              setVisibility(value);
            }
          }}
        >
          <SelectTrigger>
            <SelectValue>{toTitleCase(visibility)}</SelectValue>
          </SelectTrigger>
          <SelectPopup>
            {visibilityOptions.map((opt) => (
              <SelectItem key={opt.value} value={opt.value} label={opt.label}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectPopup>
        </Select>
      </div>

      {isSmart && (
        <div className="flex flex-col gap-5 border-t border-neutral-200 pt-6">
          <Typography level="H4">Filter</Typography>
          <EventFilterBuilder
            draft={draft}
            onChange={setDraft}
            searchTags={searchTags}
            systemTags={systemTags}
            searchOrganizers={handleSearchOrganizers}
          />
          <EventFilterMatchCount
            state={
              previewState.kind === 'idle'
                ? {kind: 'idle'}
                : previewState.kind === 'loading'
                  ? {kind: 'loading'}
                  : previewState.kind === 'error'
                    ? {kind: 'error'}
                    : {kind: 'loaded', totalCount: previewState.totalCount}
            }
            onPreview={() => setPreviewDrawerOpen(true)}
          />
        </div>
      )}
      {!isSmart && (
        <ManualListEventEditor
          initialItems={listData.items}
          onDirtyChange={handleEventDirtyChange}
          resetRef={eventResetRef}
        />
      )}
    </form>
  );

  return (
    <div className="flex flex-col gap-6 justify-center">
      <div className="flex items-center gap-3">
        <Link
          to="/dashboard/lists/$listId"
          params={{listId}}
          search={{page: 1, pageSize: 12}}
        >
          <Button variant="ghost" size="icon" aria-label="Back to list">
            <ArrowLeft className="h-5 w-5" />
          </Button>
        </Link>
        <Typography level="H3">Edit List</Typography>
      </div>

      {isSmart ? (
        <div className="flex flex-col lg:flex-row lg:gap-8">
          <main className="flex-1 min-w-0 pb-24 lg:pb-24 flex flex-col gap-5">
            {editColumn}
          </main>
          <EventFilterPreviewSidebar
            state={previewState}
            cap={PREVIEW_CAP}
            renderEvent={renderEventCard}
          />

          <MobilePreviewDrawer
            open={previewDrawerOpen}
            onOpenChange={setPreviewDrawerOpen}
            state={previewState}
            cap={PREVIEW_CAP}
            renderEvent={renderEventCard}
          />
        </div>
      ) : (
        <div>{editColumn}</div>
      )}

      <div className="sticky bottom-0 z-10 rounded-md shadow-md bg-white px-6 py-3">
        <div className="flex justify-end gap-2">
          <Link
            to="/dashboard/lists/$listId"
            params={{listId}}
            search={{page: 1, pageSize: 12}}
          >
            <Button variant="outline" type="button">
              Cancel
            </Button>
          </Link>
          <Button
            type="submit"
            form="edit-list-form"
            disabled={loading || !canSave || !name.trim()}
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            {loading ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>

      <AlertDialog open={blocker.status === 'blocked'}>
        <AlertDialogPopup>
          <AlertDialogHeader>
            <AlertDialogTitle>Unsaved changes</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Are you sure you want to leave?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogClose>
              <Button variant="outline" onClick={blocker.reset}>
                Stay
              </Button>
            </AlertDialogClose>
            <Button variant="destructive" onClick={blocker.proceed}>
              Leave
            </Button>
          </AlertDialogFooter>
        </AlertDialogPopup>
      </AlertDialog>
    </div>
  );
}

/** Shown when getEventListForEdit throws (not found, not owner, Saved Events). */
function EditListNotFound() {
  return (
    <div className="flex flex-col items-center gap-4 py-12">
      <Typography level="H3" className="text-slate-900">
        List not found
      </Typography>
      <Typography level="B1" className="text-slate-500">
        This list doesn&apos;t exist or you don&apos;t have access to edit it.
      </Typography>
      <Link to="/dashboard/lists">
        <Button variant="outline">Back to Lists</Button>
      </Link>
    </div>
  );
}
